import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, AUTOCOMPLETE, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const DCStatusMappingForm = NiceModal.create((props) => {
	const { mode, modalName, row } = props;
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const dispatch = useDispatch();

	const [isLoading, setIsLoading] = useState(false);

	const schema = useMemo(
		() =>
			yup.object().shape({
				deliveryCompId: yup.string().nullable().required(),
				dcStatus: yup.string().nullable().required(),
				otoOrderStatusId: yup.string().nullable().required(),
				otoStatus: yup.string().nullable().required(),
				otoStatusAction: yup.string().nullable().required(),
				previousStatusesIds: yup.array().of(yup.number()).nullable()
			}),
		[]
	);

	const fillForm = async () => {
		const defaultValues = {
			deliveryCompId: null,
			dcStatus: null,
			otoOrderStatusId: null,
			otoStatus: null,
			otoStatusAction: null,
			previousStatusesIds: []
		};

		if (!row) {
			return defaultValues;
		}

		try {
			const response = await axiosPrivate.post(ADMIN.STATUS_MANAGEMENT.GET_DC_STATUS_MAPPING, {
				id: row.id
			});

			const { data, status } = response;

			if (status === 200) {
				return {
					...defaultValues,
					...data[0]
				};
			}
		} catch (error) {
			AppLogger.error(error);
			showError(error);
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});
	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const onSubmit = useCallback(async (data) => {
		setIsLoading(true);
		const URL =
			mode === 'add'
				? ADMIN.STATUS_MANAGEMENT.ADD_DC_STATUS_MAPPING
				: ADMIN.STATUS_MANAGEMENT.EDIT_DC_STATUS_MAPPING;

		try {
			const response = await axiosPrivate.post(URL, {
				...data,
				id: row?.id
			});

			if (response.status === 200) {
				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.DC_STATUS_MAPPING_LIST));
				handleCloseModal();
			} else {
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				message: error?.response?.data?.message
			});
		} finally {
			setIsLoading(false);
		}
	}, []);

	const modalTitle = useMemo(() => {
		switch (mode) {
			case 'view':
				return 'viewDCStatusMapping';
			case 'edit':
				return 'editDCStatusMapping';
			case 'add':
				return 'addDCStatusMapping';
			default:
				return 'viewDCStatusMapping';
		}
	}, [mode]);

	return (
		<Modal fullwidth name={modalName} maxWidth="small" className="modal-brand">
			<ModalTitle>
				<Typography variant="p1">{modalTitle}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="deliveryCompanyName" required isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ALL_DELIVERY_COMPANIES}
							name="deliveryCompId"
							value={watch('deliveryCompId')}
							setValue={(value) => {
								setValue('deliveryCompId', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							viewMode={mode === 'view'}
							columns={[{ accessor: 'Name', width: 200 }]}
							selectionLabelAccessor={['Name']}
						/>
					</FormItem>
					<FormItem label="dcStatus" required isLoading={isLoading}>
						<StringInput name={'dcStatus'} disabled={mode === 'view'} />
					</FormItem>
					<FormItem label="dcStatus.conditions" isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.OTO_STATUSES}
							name="previousStatusesIds"
							value={watch('previousStatusesIds')}
							multipleSelection
							setValue={(value) => {
								setValue('previousStatusesIds', value?.map((item) => item?.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							disabled={mode === 'view'}
							columns={[{ accessor: 'otoStatus', width: 200 }]}
							selectionLabelAccessor={['otoStatus']}
						/>
					</FormItem>
					<FormItem label="otoOrderStatus" required isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.OTO_STATUSES}
							name="otoOrderStatusId"
							value={watch('otoOrderStatusId')}
							setValue={(value) => {
								setValue('otoOrderStatusId', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue('otoStatus', value?.otoStatus ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							viewMode={mode === 'view'}
							columns={[
								{ accessor: 'ID', width: 200 },
								{ accessor: 'otoStatus', width: 200 }
							]}
							selectionLabelAccessor={['otoStatus']}
						/>
					</FormItem>
					<FormItem label="otoStatusAction" required isLoading={isLoading}>
						<EnumSelect
							searchable={false}
							name="otoStatusAction"
							enumName={ENUMS.OTO_OMS_STATUS}
							setValue={(value) => {
								setValue(`otoStatusAction`, value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch(`otoStatusAction`)}
							viewMode={mode === 'view'}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{mode !== 'view' && (
					<Button
						label={mode === 'edit' ? 'edit' : 'add'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.error(errors);
						})}
						iconPos="right"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default DCStatusMappingForm;
